/**
 * @file 재무 API
 * 대시보드, 거래 내역, 프로젝트 손익(P&L), 미수금, 결재 연동, 예산 관리 등을 처리합니다.
 */

import { api } from "../../../util/api";
import { formatCompactKrw, formatKrw, formatNumberWithCommas } from "../../../util/numberFormat";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const financeApi = {
    // Dashboard
    /** 재무 대시보드 데이터 조회 (year/month 미전달 시 이번달) */
    getDashboard: (year, month) => api.get(`${API_BASE}/finance/dashboard`,
        year && month ? { year, month } : {}),

    // Transactions (실적 = 완료된 자금계획, 조회 전용 — 등록/수정은 현금흐름표에서 상태로 관리)
    /** 확정 매출/지출(실적) 목록 조회 — params: 날짜/유형/부서 필터 */
    getTransactions:      (params = {})             => api.get(`${API_BASE}/finance/transactions`, params),

    /** 기간 수입/지출 요약 통계 (팀별) */
    getTransactionSummary: (from, to)               => api.get(`${API_BASE}/finance/transactions/summary`, { from, to }),

    // Projects P&L
    /** 프로젝트 손익 목록 조회 (페이지네이션)
     * @param {number} [page=0] - 페이지 번호 (0-based)
     * @param {number} [size=20] - 페이지 크기
     */
    getProjects: (page = 0, size = 20) => api.get(`${API_BASE}/finance/projects`, { page, size }),

    /** 전체 프로젝트 요약 카드 목록 조회 (거래 내역 제외) */
    getProjectSummaries:  ()                        => api.get(`${API_BASE}/finance/projects/summaries`),

    /** 특정 프로젝트 손익 요약 조회
     * @param {number} channelId - 채널(프로젝트) ID
     */
    getProject:           (channelId)               => api.get(`${API_BASE}/finance/projects/${channelId}`),

    /** 특정 프로젝트 거래 내역 페이지네이션 조회
     * @param {number} channelId - 채널 ID
     * @param {number} [page=0] - 페이지 번호
     * @param {number} [size=10] - 페이지 크기
     */
    getProjectTransactions: (channelId, page = 0, size = 10) =>
        api.get(`${API_BASE}/finance/projects/${channelId}/transactions`, { page, size }),

    /** 재무에 연결 가능한 채널(프로젝트) 목록 조회 */
    getProjectChannels:   ()                        => api.get(`${API_BASE}/finance/projects/channels`),

    // Receivables
    /** 미수금 목록 조회
     * @param {boolean} [outstandingOnly=false] - true이면 미결제 항목만 조회
     */
    getReceivables:       (outstandingOnly = false) => api.get(`${API_BASE}/finance/receivables`, { outstandingOnly }),

    /** 미수금 등록
     * @param {object} data - 미수금 데이터
     */
    createReceivable:     (data)                    => api.post(`${API_BASE}/finance/receivables`, data),

    /** 미수금 수정
     * @param {number} id - 미수금 ID
     * @param {object} data - 수정할 미수금 데이터
     */
    updateReceivable:     (id, data)                => api.put(`${API_BASE}/finance/receivables/${id}`, data),

    /** 미수금 삭제
     * @param {number} id - 미수금 ID
     */
    deleteReceivable:     (id)                      => api.delete(`${API_BASE}/finance/receivables/${id}`),

    // Approval Links
    /** 재무 연동 결재 목록 조회
     * @param {boolean} [pendingOnly=false] - true이면 미처리 항목만 조회
     */
    getApprovalLinks:     (pendingOnly = false)     => api.get(`${API_BASE}/finance/approval-links`, { pendingOnly }),

    /** 재무 담당자용 결재 목록 조회 */
    getFinanceApprovals:  ()                        => api.get(`${API_BASE}/finance/approval-links/finance-approvals`),

    /** 재무 관련 전체 결재 뷰 조회 */
    getAllApprovalViews:   ()                        => api.get(`${API_BASE}/finance/approval-links/approvals`),

    /** 결재 최종 승인 (재무 확정) — bankAccountId 지정 시 자금일보에 출금 자동 반영
     * @param {number} approvalId - 결재 ID
     * @param {number|null} [bankAccountId] - 자금일보 반영 계좌
     */
    finalApproveApproval: (approvalId, bankAccountId = null) =>
        api.post(`${API_BASE}/finance/approval-links/approvals/${approvalId}/confirm${bankAccountId ? `?bankAccountId=${bankAccountId}` : ""}`),

    /** 결재 최종 반려
     * @param {number} approvalId - 결재 ID
     */
    finalRejectApproval:  (approvalId)              => api.post(`${API_BASE}/finance/approval-links/approvals/${approvalId}/reject`),

    // Budgets
    /** 예산 목록 조회
     * @param {number} year - 조회 연도
     * @param {number} month - 조회 월
     */
    getBudgets:           (year, month)             => api.get(`${API_BASE}/finance/budgets`, { year, month }),

    /** 예산 저장 (생성 또는 수정)
     * @param {object} data - 예산 데이터
     */
    saveBudget:           (data)                    => api.post(`${API_BASE}/finance/budgets`, data),

    /** 예산 삭제
     * @param {number} id - 예산 ID
     */
    deleteBudget:         (id)                      => api.delete(`${API_BASE}/finance/budgets/${id}`),

    /** 부서 목록 조회 */
    getDepartments:       ()                        => api.get(`${API_BASE}/employees/departments`),

    // ─── 거래처 (Vendor) ───────────────────────────────────────
    /** 거래처 검색·페이지 — params: { type, q, page, size } */
    listVendors:          (params)                  => api.get(`${API_BASE}/finance/vendors`, params),
    /** 자동완성 — params: { q, size } */
    suggestVendors:       (params)                  => api.get(`${API_BASE}/finance/vendors/suggest`, params),
    /** 거래처 단건 */
    getVendor:            (id)                      => api.get(`${API_BASE}/finance/vendors/${id}`),
    /** 거래처 생성 */
    createVendor:         (data)                    => api.post(`${API_BASE}/finance/vendors`, data),
    /** 거래처 수정 */
    updateVendor:         (id, data)                => api.put(`${API_BASE}/finance/vendors/${id}`, data),
    /** 거래처 삭제 (soft) */
    deleteVendor:         (id)                      => api.delete(`${API_BASE}/finance/vendors/${id}`),
    /**
     * 사업자등록증·통장사본 업로드.
     * @param {number} id - 거래처 ID
     * @param {"license"|"bankbook"} type
     * @param {File} file
     */
    uploadVendorImage: (id, type, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.upload(`${API_BASE}/finance/vendors/${id}/image?type=${encodeURIComponent(type)}`, fd);
    },

    /** 자유 첨부 목록 */
    listVendorAttachments: (id) => api.get(`${API_BASE}/finance/vendors/${id}/attachments`),
    /** 자유 첨부 추가 (이미지·PDF 등) */
    addVendorAttachment: (id, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.upload(`${API_BASE}/finance/vendors/${id}/attachments`, fd);
    },
    /** 자유 첨부 삭제 (soft) */
    deleteVendorAttachment: (attachmentId) =>
        api.delete(`${API_BASE}/finance/vendors/attachments/${attachmentId}`),

    // ─── 현금흐름표 (자금수지) ──────────────────────────────────
    /** 현금흐름표 타임라인 — params: { from, to, deptId, includeCompleted, includeHold } */
    getCashFlow:              (params = {})  => api.get(`${API_BASE}/finance/cashflow`, params),
    /** 자금계획 수기 등록 */
    createCashPlan:           (data)         => api.post(`${API_BASE}/finance/cashflow`, data),
    /** 자금계획 수정 */
    updateCashPlan:           (id, data)     => api.put(`${API_BASE}/finance/cashflow/${id}`, data),
    /** 자금계획 삭제 */
    deleteCashPlan:           (id)           => api.delete(`${API_BASE}/finance/cashflow/${id}`),
    /** 자금계획 일괄 삭제 (완료 건 제외) */
    bulkDeleteCashPlans:      (ids)          => api.post(`${API_BASE}/finance/cashflow/bulk-delete`, { ids }),
    /** 상태 변경 (예정/우선지급/지급확정/홀딩/완료) — 완료 시 실적 거래 생성.
     *  완료 시 bankAccountId(입출금 계좌)·transactionDate(거래일) 지정하면 자금일보에 자동 반영. */
    changeCashPlanStatus:     (id, status, bankAccountId = null, transactionDate = null) =>
        api.post(`${API_BASE}/finance/cashflow/${id}/status`, { status, bankAccountId, transactionDate }),
    /** 상태 일괄 변경 (원자적) — items: [{rowKey, status, bankAccountId?, transactionDate?}]. 하나라도 실패하면 전면 롤백.
     *  rowKey: 저장 행은 숫자 id 문자열, 라이브(파생) 행은 "APPROVAL:{id}" / "RECEIVABLE:{id}". */
    batchChangeCashPlanStatus:(items)         => api.post(`${API_BASE}/finance/cashflow/status/batch`, { items }),
    /** 고정비 템플릿을 해당 월 지출 예정으로 생성 */
    generateRecurring:        (year, month)  => api.post(`${API_BASE}/finance/cashflow/recurring/generate?year=${year}&month=${month}`),
    /** 고정비 템플릿 목록 */
    getRecurringTemplates:    ()             => api.get(`${API_BASE}/finance/cashflow/recurring-templates`),
    createRecurringTemplate:  (data)         => api.post(`${API_BASE}/finance/cashflow/recurring-templates`, data),
    updateRecurringTemplate:  (id, data)     => api.put(`${API_BASE}/finance/cashflow/recurring-templates/${id}`, data),
    deleteRecurringTemplate:  (id)           => api.delete(`${API_BASE}/finance/cashflow/recurring-templates/${id}`),
    // 기준 잔액은 자금일보 최신 일마감 총액에서 서버가 자동 파생한다(getCashFlow.baseBalance) — 설정·연동 API 없음.

    // ─── 자금일보 ───────────────────────────────────────────────
    /** 자금일보 조회 — 일자별 계좌 입출금·잔액 + 대출 현황 */
    getDailyCash:             (date)         => api.get(`${API_BASE}/finance/daily-cash`, date ? { date } : {}),
    addDailyCashEntry:        (data)         => api.post(`${API_BASE}/finance/daily-cash/entries`, data),
    updateDailyCashEntry:     (id, data)     => api.put(`${API_BASE}/finance/daily-cash/entries/${id}`, data),
    deleteDailyCashEntry:     (id)           => api.delete(`${API_BASE}/finance/daily-cash/entries/${id}`),
    /** 일마감 — { date, overrides: [{ accountId, closingBalance }] } */
    closeDailyCash:           (data)         => api.post(`${API_BASE}/finance/daily-cash/close`, data),
    reopenDailyCash:          (date)         => api.post(`${API_BASE}/finance/daily-cash/reopen?date=${date}`),
    getBankAccounts:          ()             => api.get(`${API_BASE}/finance/daily-cash/accounts`),
    createBankAccount:        (data)         => api.post(`${API_BASE}/finance/daily-cash/accounts`, data),
    updateBankAccount:        (id, data)     => api.put(`${API_BASE}/finance/daily-cash/accounts/${id}`, data),
    deleteBankAccount:        (id)           => api.delete(`${API_BASE}/finance/daily-cash/accounts/${id}`),
    reorderBankAccounts:      (ids)          => api.post(`${API_BASE}/finance/daily-cash/accounts/reorder`, ids),
    getLoans:                 ()             => api.get(`${API_BASE}/finance/daily-cash/loans`),
    createLoan:               (data)         => api.post(`${API_BASE}/finance/daily-cash/loans`, data),
    updateLoan:               (id, data)     => api.put(`${API_BASE}/finance/daily-cash/loans/${id}`, data),
    deleteLoan:               (id)           => api.delete(`${API_BASE}/finance/daily-cash/loans/${id}`),
};

export const fmtKRW     = (n) => formatCompactKrw(n);
export const fmtKRWFull = (n) => formatKrw(n);
export const fmtComma   = (n) => formatNumberWithCommas(n, { emptyZero: true });

/**
 * 자금일보 응답 → 계좌별 '실시간' 잔액 맵 { accountId: 잔액 }.
 * 마감된 계좌는 마감잔액(closedBalance), 미마감은 오늘 입출금까지 반영한 계산잔액(computedClosing).
 * (백엔드 변경 없이 기존 자금일보 조회 결과로 실시간 출금가능액을 구할 때 사용)
 */
export const dailyAccountBalances = (report) => {
    const m = {};
    (report?.accounts || []).forEach(ar => {
        if (ar?.account?.id != null) m[ar.account.id] = ar.closed ? ar.closedBalance : ar.computedClosing;
    });
    return m;
};
